/**
 * Tidio chat layering override
 *
 * Tidio injects #tidio-chat with an inline z-index of 999999999 !important,
 * which stacks above theme drawers and commerce UI. Stylesheet rules cannot
 * beat inline !important, so this script re-applies theme-safe layering after
 * the widget loads and hides the widget when overlays or sticky controls are active.
 *
 * Keyboard focus inside the widget is drawn outside the control. Tidio replaces
 * outline, so the ring is an outset box-shadow on every keyboard-focusable
 * control in the open #tidio-chat shadow, or the iframe when that build is present.
 */
(function () {
  'use strict';

  const TIDIO_Z_INDEX = '15';
  const TIDIO_OPEN_Z_INDEX = '30';
  const CHAT_OPEN_ATTR = 'data-shelfology-chat-open';
  const TIDIO_SELECTORS = ['#tidio-chat', '#tidio-chat-iframe'];
  const STYLE_SNAPSHOT_ATTR = 'data-tidio-theme-style-snapshot';
  const THEME_HIDDEN_ATTR = 'data-tidio-theme-hidden';
  const OVERLAY_SELECTOR = [
    '.drawer[open]',
    '.drawer[active]',
    '.quick-view[open]',
    '.quick-view[active]',
    '.route-modal.rm-open',
  ].join(', ');

  let initialized = false;
  let discoveryObserver = null;
  let observedRouteModal = null;
  let routeModalClassObserver = null;
  let routeModalDiscoveryObserver = null;
  const tidioStyleObservers = new Map();

  const getTidioElements = () => {
    return TIDIO_SELECTORS.flatMap((selector) => [...document.querySelectorAll(selector)]);
  };

  const isStickyCommerceVisible = () => {
    const stickyCard = document.querySelector('.product-sticky-form__card');

    if (stickyCard) {
      const style = getComputedStyle(stickyCard);

      if (style.visibility === 'visible' && Number.parseFloat(style.opacity) > 0) {
        return true;
      }
    }

    return Boolean(document.querySelector('.product-bundle-wrapper > .lg\\:grow-0.active'));
  };

  const shouldHideTidio = () => {
    if (document.body.classList.contains('has-modal-open')) {
      return true;
    }

    if (document.querySelector(OVERLAY_SELECTOR)) {
      return true;
    }

    return isStickyCommerceVisible();
  };

  const readInlineStyle = (element, property) => ({
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
  });

  const restoreInlineStyle = (element, property, snapshot) => {
    if (!snapshot?.value) {
      element.style.removeProperty(property);
      return;
    }

    element.style.setProperty(property, snapshot.value, snapshot.priority);
  };

  const captureStyleSnapshot = (element) => {
    if (element.hasAttribute(STYLE_SNAPSHOT_ATTR)) {
      return;
    }

    element.setAttribute(
      STYLE_SNAPSHOT_ATTR,
      JSON.stringify({
        visibility: readInlineStyle(element, 'visibility'),
        pointerEvents: readInlineStyle(element, 'pointer-events'),
      })
    );
  };

  const restoreStyleSnapshot = (element) => {
    const rawSnapshot = element.getAttribute(STYLE_SNAPSHOT_ATTR);

    if (!rawSnapshot) {
      return;
    }

    try {
      const snapshot = JSON.parse(rawSnapshot);
      restoreInlineStyle(element, 'visibility', snapshot.visibility);
      restoreInlineStyle(element, 'pointer-events', snapshot.pointerEvents);
    } finally {
      element.removeAttribute(STYLE_SNAPSHOT_ATTR);
    }
  };

  const applyToElement = (element) => {
    // Shelfology Custom: 30 while chat is open and drawers are not hiding the widget; otherwise 15.
    const zIndex = element.hasAttribute(CHAT_OPEN_ATTR) && !shouldHideTidio()
      ? TIDIO_OPEN_Z_INDEX
      : TIDIO_Z_INDEX;

    if (element.style.getPropertyValue('z-index') !== zIndex) {
      element.style.setProperty('z-index', zIndex, 'important');
    }

    const hidden = shouldHideTidio();
    const themeHidden = element.hasAttribute(THEME_HIDDEN_ATTR);

    if (hidden) {
      if (!themeHidden) {
        captureStyleSnapshot(element);
        element.setAttribute(THEME_HIDDEN_ATTR, '');
      }

      element.style.setProperty('visibility', 'hidden', 'important');
      element.style.setProperty('pointer-events', 'none', 'important');
      return;
    }

    if (themeHidden) {
      element.removeAttribute(THEME_HIDDEN_ATTR);
      restoreStyleSnapshot(element);
    }
  };

  const syncTidio = () => {
    getTidioElements().forEach(applyToElement);
  };

  const observeTidioElement = (element) => {
    if (tidioStyleObservers.has(element)) {
      return;
    }

    const observer = new MutationObserver(() => {
      applyToElement(element);
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['style', CHAT_OPEN_ATTR],
    });

    tidioStyleObservers.set(element, observer);
  };

  // Shelfology Custom: a host can appear after discovery stops. The open flag is the signal to attach and choose 30 vs 15.
  const watchChatOpenFlag = () => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const element = mutation.target;

        if (element.nodeType !== 1 || !TIDIO_SELECTORS.some((selector) => element.matches(selector))) {
          continue;
        }

        observeTidioElement(element);
        applyToElement(element);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      subtree: true,
      attributeFilter: [CHAT_OPEN_ATTR],
    });
  };

  const disconnectDiscoveryObserver = () => {
    if (!discoveryObserver) {
      return;
    }

    discoveryObserver.disconnect();
    discoveryObserver = null;
  };

  // Keyboard focus ring outside each Tidio control. Theme CSS cannot reach the
  // shadow tree or iframe. :focus-visible only, so a click does not draw it.
  const FOCUS_STYLE_ID = 'shelfology-tidio-focus-ring';
  const FOCUS_CONTROL_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    'summary',
  ].join(', ');
  const FOCUS_RING_CSS = [
    `:is(${FOCUS_CONTROL_SELECTOR}):focus-visible {`,
    '  box-shadow: 0 0 0 2px #fffdfb, 0 0 0 5px rgb(var(--color-keyboard-focus)) !important;',
    '}',
  ].join('\n');
  const shadowWatchers = new WeakSet();
  const frameWatchers = new WeakSet();
  const ringListeners = new WeakSet();
  const ringedButtons = new WeakSet();
  let focusDiscoveryObserver = null;
  let shadowRetry = null;

  const keyboardFocusChannels = () => {
    return getComputedStyle(document.documentElement).getPropertyValue('--color-keyboard-focus').trim();
  };

  const focusRingValue = () => {
    return `0 0 0 2px #fffdfb, 0 0 0 5px rgb(${keyboardFocusChannels()})`;
  };

  const widgetControl = (node) => {
    const element = node?.nodeType === 1 ? node : node?.parentElement;

    return element?.closest?.(FOCUS_CONTROL_SELECTOR) || null;
  };

  // Tidio writes its own box-shadow on keyboard focus, which beats a stylesheet
  // rule. Re-apply the outside ring inline, and only while :focus-visible.
  const paintLauncherRing = (control) => {
    if (!control?.matches?.(FOCUS_CONTROL_SELECTOR)) {
      return;
    }

    if (!control.matches(':focus-visible') || !keyboardFocusChannels()) {
      if (ringedButtons.has(control)) {
        control.style.removeProperty('box-shadow');
        control.style.removeProperty('transition');
        ringedButtons.delete(control);
      }

      return;
    }

    const value = focusRingValue();

    if (
      control.style.getPropertyValue('box-shadow') === value
      && control.style.getPropertyPriority('box-shadow') === 'important'
      && control.style.getPropertyValue('transition') === 'none'
    ) {
      return;
    }

    control.style.setProperty('transition', 'none', 'important');
    control.style.setProperty('box-shadow', value, 'important');
    ringedButtons.add(control);
  };

  const watchLauncherFocus = (root) => {
    if (!root || ringListeners.has(root)) {
      return;
    }

    ringListeners.add(root);

    const paint = (event) => {
      const control = widgetControl(event.target);

      if (!control) {
        return;
      }

      paintLauncherRing(control);
      window.requestAnimationFrame(() => paintLauncherRing(control));
    };

    root.addEventListener('focusin', paint, true);
    root.addEventListener('focusout', paint, true);
  };

  const applyFocusStyle = (root) => {
    const ownerDoc = root.nodeType === 9 ? root : root.ownerDocument;
    let style = root.getElementById?.(FOCUS_STYLE_ID) || null;

    if (!style) {
      style = ownerDoc.createElement('style');
      style.id = FOCUS_STYLE_ID;
      (root.head || root).appendChild(style);
    }

    if (style.textContent !== FOCUS_RING_CSS) {
      style.textContent = FOCUS_RING_CSS;
    }

    return true;
  };

  const injectTidioShadowRing = (host) => {
    const shadow = host?.shadowRoot;

    if (!shadow) {
      return false;
    }

    const channels = keyboardFocusChannels();

    if (!channels) {
      return false;
    }

    if (host.style.getPropertyValue('--color-keyboard-focus').trim() !== channels) {
      host.style.setProperty('--color-keyboard-focus', channels);
    }

    watchLauncherFocus(shadow);
    return applyFocusStyle(shadow);
  };

  const watchTidioShadow = (host) => {
    const shadow = host?.shadowRoot;

    if (!shadow || shadowWatchers.has(shadow)) {
      return;
    }

    shadowWatchers.add(shadow);
    new MutationObserver(() => {
      if (!shadow.getElementById(FOCUS_STYLE_ID)) {
        injectTidioShadowRing(host);
      }
    }).observe(shadow, { childList: true });
  };

  const ensureTidioShadowRing = () => {
    const host = document.getElementById('tidio-chat');

    if (!host) {
      return;
    }

    if (injectTidioShadowRing(host)) {
      watchTidioShadow(host);

      if (shadowRetry) {
        window.clearInterval(shadowRetry);
        shadowRetry = null;
      }

      return;
    }

    if (shadowRetry || host.shadowRoot) {
      return;
    }

    let attempts = 0;
    shadowRetry = window.setInterval(() => {
      attempts += 1;
      const current = document.getElementById('tidio-chat');

      if (!current || attempts >= 40) {
        window.clearInterval(shadowRetry);
        shadowRetry = null;
      }

      if (current?.shadowRoot && injectTidioShadowRing(current)) {
        watchTidioShadow(current);
        window.clearInterval(shadowRetry);
        shadowRetry = null;
      }
    }, 500);
  };

  const injectTidioFrameRing = (iframe) => {
    let doc;

    try {
      doc = iframe.contentDocument;
    } catch {
      return false;
    }

    const head = doc?.head || doc?.documentElement;

    if (!head) {
      return false;
    }

    const channels = keyboardFocusChannels();

    if (!channels) {
      return false;
    }

    doc.documentElement.style.setProperty('--color-keyboard-focus', channels);
    watchLauncherFocus(doc);
    return applyFocusStyle(doc);
  };

  const watchTidioFrame = (iframe) => {
    if (!iframe || frameWatchers.has(iframe)) {
      return;
    }

    frameWatchers.add(iframe);

    const apply = () => {
      if (!injectTidioFrameRing(iframe)) {
        return;
      }

      let doc;

      try {
        doc = iframe.contentDocument;
      } catch {
        return;
      }

      const head = doc?.head;

      if (!head || head.dataset.tidioFocusWatch === 'true') {
        return;
      }

      head.dataset.tidioFocusWatch = 'true';
      new MutationObserver(() => {
        if (!doc.getElementById(FOCUS_STYLE_ID)) {
          injectTidioFrameRing(iframe);
        }
      }).observe(head, { childList: true });
    };

    iframe.addEventListener('load', apply);
    apply();
  };

  const ensureTidioFocusRing = () => {
    ensureTidioShadowRing();
    watchTidioFrame(document.getElementById('tidio-chat-iframe'));
  };

  const startFocusDiscovery = () => {
    ensureTidioFocusRing();

    if (focusDiscoveryObserver) {
      return;
    }

    focusDiscoveryObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') {
          continue;
        }

        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) {
            continue;
          }

          if (
            node.id === 'tidio-chat'
            || node.id === 'tidio-chat-iframe'
            || node.querySelector?.('#tidio-chat, #tidio-chat-iframe')
          ) {
            ensureTidioFocusRing();
            return;
          }
        }
      }
    });

    focusDiscoveryObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.setTimeout(() => {
      if (!focusDiscoveryObserver) {
        return;
      }

      focusDiscoveryObserver.disconnect();
      focusDiscoveryObserver = null;
      ensureTidioFocusRing();
    }, 60000);
  };

  const attachToTidioElements = () => {
    const elements = getTidioElements();

    if (!elements.length) {
      return false;
    }

    elements.forEach((element) => {
      observeTidioElement(element);
      applyToElement(element);
    });

    ensureTidioFocusRing();
    disconnectDiscoveryObserver();
    return true;
  };

  const startDiscoveryObserver = () => {
    if (discoveryObserver) {
      return;
    }

    discoveryObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') {
          continue;
        }

        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) {
            continue;
          }

          if (TIDIO_SELECTORS.some((selector) => node.matches?.(selector) || node.querySelector?.(selector))) {
            attachToTidioElements();
            return;
          }
        }
      }
    });

    discoveryObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.setTimeout(disconnectDiscoveryObserver, 30000);
  };

  const resetRouteModalObserver = () => {
    if (routeModalClassObserver) {
      routeModalClassObserver.disconnect();
      routeModalClassObserver = null;
    }

    observedRouteModal = null;
  };

  const findRouteModalInNode = (node) => {
    if (node.nodeType !== 1) {
      return null;
    }

    if (node.classList.contains('route-modal')) {
      return node;
    }

    return node.querySelector?.('.route-modal') || null;
  };

  const watchRouteModalElement = (modal) => {
    if (!modal || modal === observedRouteModal) {
      return;
    }

    resetRouteModalObserver();
    observedRouteModal = modal;

    routeModalClassObserver = new MutationObserver(syncTidio);
    routeModalClassObserver.observe(modal, {
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  const routeModalMutationIsRelevant = (mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target.classList?.contains('route-modal')) {
        return true;
      }

      if (mutation.type !== 'childList') {
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (findRouteModalInNode(node)) {
          return true;
        }
      }

      for (const node of mutation.removedNodes) {
        if (node.nodeType !== 1) {
          continue;
        }

        if (node === observedRouteModal || findRouteModalInNode(node)) {
          return true;
        }
      }
    }

    return false;
  };

  const syncRouteModalTracking = () => {
    if (observedRouteModal && !document.contains(observedRouteModal)) {
      resetRouteModalObserver();
    }

    watchRouteModalElement(document.querySelector('.route-modal'));
    syncTidio();
  };

  const startRouteModalTracking = () => {
    watchRouteModalElement(document.querySelector('.route-modal'));

    if (routeModalDiscoveryObserver) {
      return;
    }

    routeModalDiscoveryObserver = new MutationObserver((mutations) => {
      const observedMissing = observedRouteModal && !document.contains(observedRouteModal);

      if (!routeModalMutationIsRelevant(mutations) && !observedMissing) {
        return;
      }

      syncRouteModalTracking();
    });

    routeModalDiscoveryObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  const observeOverlayState = () => {
    document.addEventListener('modal:afterShow', syncTidio);
    document.addEventListener('modal:afterHide', syncTidio);

    new MutationObserver(syncTidio).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    const stickyCard = document.querySelector('.product-sticky-form__card');

    if (stickyCard) {
      new MutationObserver(syncTidio).observe(stickyCard, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    const bundleSidebar = document.querySelector('.product-bundle-wrapper > .lg\\:grow-0');

    if (bundleSidebar) {
      new MutationObserver(syncTidio).observe(bundleSidebar, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    const hasStickyCommerceUI = document.querySelector('.product-sticky-form, .product-bundle-wrapper');

    if (hasStickyCommerceUI) {
      let scrollRaf = null;

      window.addEventListener(
        'scroll',
        () => {
          if (scrollRaf) {
            return;
          }

          scrollRaf = window.requestAnimationFrame(() => {
            scrollRaf = null;
            syncTidio();
          });
        },
        { passive: true }
      );
    }

    startRouteModalTracking();
  };

  const init = () => {
    if (!attachToTidioElements()) {
      startDiscoveryObserver();
    }

    if (initialized) {
      syncTidio();
      return;
    }

    initialized = true;
    observeOverlayState();
    watchChatOpenFlag();
    syncTidio();
    startFocusDiscovery();

    let attempts = 0;
    const poll = window.setInterval(() => {
      attempts += 1;

      if (attachToTidioElements() || attempts >= 20) {
        window.clearInterval(poll);
      }
    }, 500);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.tidioChatApi) {
    window.tidioChatApi.on('ready', init);
  } else {
    document.addEventListener('tidioChat-ready', init);
  }
})();
